import { Inject, Injectable } from '@angular/core';
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AccountInfo,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { UserStore } from '../../../shared/data-access/stores/user.store';
import { observe } from '../../../shared/utils/functions/observe.function';
import { CacheService } from '../../../shared/data-access/services/cache.service';
import { cacheKeysEnum } from '../../../shared/enums/cacheKeysEnum';
import { Roles } from '../models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _destroying$ = new Subject<void>();
  private readonly _activeAccount$ = new BehaviorSubject<AccountInfo | null>(null);
  private _isLoggedIn$ = new BehaviorSubject<boolean>(false);

  activeAccount$ = this._activeAccount$.asObservable();
  isLoggedIn$ = this._isLoggedIn$.asObservable();

  constructor(
    @Inject(MSAL_GUARD_CONFIG)
    private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userStore: UserStore,
    private cacheService: CacheService
  ) {
    this.updateLoggedInStatus();
  }

  updateLoggedInStatus() {
        this.msalBroadcastService.inProgress$
        .pipe(
          filter(
            (status: InteractionStatus) => status === InteractionStatus.None,
          ),
          takeUntil(this._destroying$),
        )
        .subscribe(() => {
          this.checkAndSetActiveAccount();
        });
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  getAccessToken(): string {
    return this.authService.instance.getActiveAccount()?.idToken ?? '';
  }

  resolveAuthState(): Observable<boolean> {
    return this.authService.handleRedirectObservable().pipe(
      map(() => {
        this.checkAndSetActiveAccount();
        return this.authService.instance.getAllAccounts().length > 0;
      })
    );
  }

  private checkAndSetActiveAccount() {
    const account = this.authService.instance.getActiveAccount() || this.authService.instance.getAllAccounts()[0] || null;

    if (account) {
      this.authService.instance.setActiveAccount(account);
      this.updateUserStore(account);
      this._isLoggedIn$.next(true);
    } else {
      this.clearUserStore();
      this._isLoggedIn$.next(false);
    }
    this._activeAccount$.next(account);
  }

  private updateUserStore(account: AccountInfo | null): void {
    if(account) {
        const emails = account.idTokenClaims?.emails
        this.userStore.set({
        name: (account?.idTokenClaims?.['given_name'] as string)|| '' ,
        surname:   (account?.idTokenClaims?.['family_name'] as string) || '',
        email: (Array.isArray(emails) ? emails[0] : emails) ?? '',
        userId: account?.idTokenClaims?.oid || '',
        rank: (account?.idTokenClaims?.['extension_Rank'] as string) || '',
        secondInCharge:
          (account?.idTokenClaims?.['extension_Secondincharge'] as string) || '',
        token: account?.idToken || '',
        university:
          (account?.idTokenClaims?.['extension_University'] as string) || '',
        yearsOfExperience: Number(
          account?.idTokenClaims?.['extension_YearsOfExperience'] || 0,
        ),
        roles: (account?.idTokenClaims?.['extension_role'] as string).split(',') as Roles[] || [],
        faculty: (account?.idTokenClaims?.['extension_Faculty'] as string) || '',
        department:
          (account?.idTokenClaims?.['extension_Department'] as string) || '',
      });
    }
  }

  private clearUserStore(): void {
    this.userStore.set({
      name: '',
      surname:'',
      email: '',
      userId: '',
      rank: '',
      secondInCharge: '',
      token: '',
      yearsOfExperience: 0,
      roles: [],
      university: '',
      faculty: '',
      department: '',
    });
  }

  logout() {
    observe(this.authService.logoutRedirect({
      postLogoutRedirectUri: '/',
    })).subscribe(data => {
      if(data) this.clearUserStore();
    });
    this.cacheService.clear(cacheKeysEnum.studentDetails);
    this.destroy();
  }

  destroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    this._activeAccount$.complete();
    this._isLoggedIn$.complete();
  }
}
